import { AbstractControl, ValidatorFn } from '@angular/forms';

import { esAlfanumerico, esSoloLetras, esEmail, esSoloLetrasNumeros } from '@global-utiles/validaciones-regexp';
import { ToastrService } from 'ngx-toastr';
import { fmt, parseISODate, parseISODateTime } from '@app/utiles/javascript';

export type CustomValidatorsVal = string | number | boolean | null;
export type CustomValidatorsString = { [key in CustomValidatorsEnum]: (val: CustomValidatorsVal) => string };

export type CustomValidatorsEnum =
  | 'required'
  | 'alfanumericoMax'
  | 'soloLetras'
  | 'numericoEntero'
  | 'numericoMax'
  | 'numericoMin'
  | 'email'
  | 'color'
  | 'soloLetrasNumeros'
  | 'longitudMaxima'
  | 'longitudMinima'
  // fechas (solo día)
  | 'fechaISO'
  | 'fechaMin'
  | 'fechaMax'
  | 'fechaEntre'
  // fecha-hora
  | 'fechaHoraISO'
  | 'fechaHoraMin'
  | 'fechaHoraMax'
  | 'fechaHoraEntre';
export const CUSTOM_VALIDATORS_STRING: CustomValidatorsString = {
  required: () => 'Requerido',
  alfanumericoMax: (val) => `Alfanumérico [${val}]`,
  soloLetras: () => `Solo letras`,
  numericoMax: (val) => `Numérico máximo [${val}]`,
  numericoMin: (val) => `Numérico mínimo [${val}]`,
  numericoEntero: () => `Numérico entero`,
  email: () => 'Email invalido',
  color: () => 'Formato color',
  soloLetrasNumeros: () => 'Solo letras y números',
  longitudMaxima: (val) => `Máximo ${val} caracteres`,
  longitudMinima: (val) => `Mínimo ${val} caracteres`,
  // mensajes fecha
  fechaISO: () => 'Formato de fecha inválido (use YYYY-MM-DD)',
  fechaMin: (val) => `La fecha debe ser igual o posterior a ${val}`,
  fechaMax: (val) => `La fecha debe ser igual o anterior a ${val}`,
  fechaEntre: (val) => `La fecha debe estar en el rango ${val}`,
  // mensajes fecha-hora
  fechaHoraISO: () => 'Formato de fecha-hora inválido (use YYYY-MM-DDTHH:mm o YYYY-MM-DDTHH:mm:ss)',
  fechaHoraMin: (val) => `La fecha-hora debe ser igual o posterior a ${val}`,
  fechaHoraMax: (val) => `La fecha-hora debe ser igual o anterior a ${val}`,
  fechaHoraEntre: (val) => `La fecha-hora debe estar en el rango ${val}`,
};
export type CustomValidatorsType = {
  [key in CustomValidatorsEnum]?: CustomValidatorsVal;
};

export class CustomValidators {
  public static required(control: AbstractControl): CustomValidatorsType | null {
    const value: string | number | string[] | number[] = control.value;
    if (value === null || value === undefined || value === '' || (typeof value === 'object' && value.length === 0)) {
      return { required: true };
    }
    return null;
  }
  public static alfanumericoMax(val: number): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value: string = control.value;
      if (!value) return null;
      if (value === '' || value.length > val || !esAlfanumerico(value)) {
        return { alfanumericoMax: val };
      }
      return null;
    };
  }
  public static numericoEntero(control: AbstractControl): CustomValidatorsType | null {
    const value: string | number = control.value;
    if (value === null || value === undefined) return null;
    if (!/^-?\d\d*$/.test(value?.toString())) {
      return { numericoEntero: true };
    }
    return null;
  }
  public static numericoMax(val: number): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value: number = control.value;
      if (value === null || value === undefined) return null;
      if (value > val) {
        return { numericoMax: val };
      }
      return null;
    };
  }
  public static numericoMin(val: number): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value: number = control.value;
      if (value === null || value === undefined) return null;
      if (value < val) {
        return { numericoMin: val };
      }
      return null;
    };
  }
  public static email(control: AbstractControl): CustomValidatorsType | null {
    const value: string = control.value;
    if (!value) return null;
    if (!esEmail(value)) {
      return { email: true };
    }
    return null;
  }
  public static color(control: AbstractControl): CustomValidatorsType | null {
    const value: string = control.value;
    if (!value) return;
    if (!/^#(?:[0-9a-fA-F]{3,4}){1,2}$/.test(value)) return { color: true };
    return null;
  }
  public static soloLetras(control: AbstractControl): CustomValidatorsType | null {
    const value: string = control.value;
    if (!value) return;
    if (!esSoloLetras(value)) return { soloLetras: true };
    return null;
  }
  public static soloLetrasNumeros(control: AbstractControl): CustomValidatorsType | null {
    const value: string = control.value;
    if (!value) return;
    if (!esSoloLetrasNumeros(value)) return { soloLetrasNumeros: true };
    return null;
  }
  public static longitudMaxima(val: number): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value: string = control.value;
      if (!value) return null;
      if (value.length > val) {
        return { longitudMaxima: val };
      }
      return null;
    };
  }
  public static longitudMinima(val: number): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value: string = control.value;
      if (!value) return null;
      if (value.length < val) {
        return { longitudMinima: val };
      }
      return null;
    };
  }

  /** Verifica formato y existencia de la fecha (YYYY-MM-DD). */
  public static fechaISO(): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') return null;
      const parsed = parseISODate(value);
      if (!parsed) return { fechaISO: true };
      return null;
    };
  }

  /** La fecha debe ser >= min (min en string ISO YYYY-MM-DD o Date). */
  public static fechaMin(min: string | Date): ValidatorFn {
    const minDate = parseISODate(min);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!minDate) return null; // si min no es válido, no forzamos error
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODate(value);
      if (!current) return { fechaISO: true };
      if (current.getTime() < minDate.getTime()) return { fechaMin: fmt(min) };
      return null;
    };
  }

  /** La fecha debe ser <= max (max en string ISO YYYY-MM-DD o Date). */
  public static fechaMax(max: string | Date): ValidatorFn {
    const maxDate = parseISODate(max);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!maxDate) return null;
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODate(value);
      if (!current) return { fechaISO: true };
      if (current.getTime() > maxDate.getTime()) return { fechaMax: fmt(max) };
      return null;
    };
  }

  /** La fecha debe estar en [min, max] (incluidos). */
  public static fechaEntre(min: string | Date, max: string | Date): ValidatorFn {
    const minDate = parseISODate(min);
    const maxDate = parseISODate(max);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!minDate || !maxDate) return null;
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODate(value);
      if (!current) return { fechaISO: true };
      if (current.getTime() < minDate.getTime() || current.getTime() > maxDate.getTime()) {
        return { fechaEntre: `${fmt(min)} – ${fmt(max)}` };
      }
      return null;
    };
  }

  // ────────────────────────────────────────────────────────────────────────────
  // 🕒 Validadores de FECHA-HORA – formato ISO YYYY-MM-DDTHH:mm[:ss]
  // ────────────────────────────────────────────────────────────────────────────

  /** Verifica formato y validez de fecha-hora (YYYY-MM-DDTHH:mm[:ss]). */
  public static fechaHoraISO(): ValidatorFn {
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') return null;
      const parsed = parseISODateTime(value);
      if (!parsed) return { fechaHoraISO: true };
      return null;
    };
  }

  /** La fecha-hora debe ser >= min. */
  public static fechaHoraMin(min: string | Date): ValidatorFn {
    const minDt = parseISODateTime(min);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!minDt) return null;
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODateTime(value);
      if (!current) return { fechaHoraISO: true };
      if (current.getTime() < minDt.getTime()) return { fechaHoraMin: fmt(min) };
      return null;
    };
  }

  /** La fecha-hora debe ser <= max. */
  public static fechaHoraMax(max: string | Date): ValidatorFn {
    const maxDt = parseISODateTime(max);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!maxDt) return null;
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODateTime(value);
      if (!current) return { fechaHoraISO: true };
      if (current.getTime() > maxDt.getTime()) return { fechaHoraMax: fmt(max) };
      return null;
    };
  }

  /** La fecha-hora debe estar en [min, max] (incluidos). */
  public static fechaHoraEntre(min: string | Date, max: string | Date): ValidatorFn {
    const minDt = parseISODateTime(min);
    const maxDt = parseISODateTime(max);
    return (control: AbstractControl): CustomValidatorsType | null => {
      const value = control.value;
      if (!minDt || !maxDt) return null;
      if (value === null || value === undefined || value === '') return null;
      const current = parseISODateTime(value);
      if (!current) return { fechaHoraISO: true };
      if (current.getTime() < minDt.getTime() || current.getTime() > maxDt.getTime()) {
        return { fechaHoraEntre: `${fmt(min)} – ${fmt(max)}` };
      }
      return null;
    };
  }
}

/**
 * Para input que no son reactivos. [(ngModel)];
 * (keypress)="oldFormRestrictions(toastr, $event, ['alfanumericoMax'], [124])"
 */
export function oldFormRestrictions<T>(toastr: ToastrService, event: KeyboardEvent, validaciones: CustomValidatorsEnum[], valores: number[] = []) {
  const currentTarget = event.currentTarget as HTMLInputElement;
  if(event.key === 'Enter') return;
  validaciones.forEach((v,i) => {
    if (v === 'alfanumericoMax') {
      if(!esAlfanumerico(event.key)) {
        toastr.warning('Solo se admiten caracteres alfanuméricos','Input inválido')
        return event.preventDefault();
      };
      if(currentTarget.value?.length > valores[i]) {
        toastr.warning('Máximo de caracteres: ' + valores[i],'Input inválido')
        return event.preventDefault();
      };
    }
  })

}
